import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, Radio, Tag, Tooltip, Alert, message } from 'antd';
import './accessKeyManagement.less';
import {
  getAccessKeyManagementList,
  CreateAccessKey,
  deleteAccessKey,
  renewAccessKey,
  upgradeAccessKeyToV3,
  updateAccessKeySecret,
  downloadAccessKeySecret,
} from '../../../api'
import { useRequest, useUpdateEffect } from 'ahooks'
import { useRecoilValue } from 'recoil'
import { userInfo as recoilUserInfo, appReady } from '../../../recoil'
import moment from 'moment';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { i18n as i } from '../../../i18n';
import { ERROR_CODE_MAP } from '../../../constants/errorCode';

const getDurationOptions = () => [
  { label: i('1年'), value: '1Y' },
  { label: i('2年'), value: '2Y' },
  { label: i('自定义'), value: 'custom' },
];

interface SecretDownloadData {
  clientSecret?: string;
  secret?: string;
  displayName?: string;
}

function addDuration(base: moment.Moment, value: string): moment.Moment {
  const m = base.clone();
  switch (value) {
    case '1Y': return m.add(1, 'y');
    case '2Y': return m.add(2, 'y');
    default: return m;
  }
}

function AccessKeyManagement() {
  const [data, setData] = useState<Array<AccessKeyManagementType.AccessKeyManagement>>([])
  const [filter, setFilter] = useState<'enabled' | 'expired'>('enabled')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const userInfo = useRecoilValue(recoilUserInfo);
  const appIsReady = useRecoilValue(appReady);

  // 创建
  const [createQuick, setCreateQuick] = useState('1Y')
  const [createExpire, setCreateExpire] = useState<moment.Moment | null>(() => addDuration(moment(), '1Y'))
  const [createForm] = Form.useForm();

  // 续期
  const [renewTarget, setRenewTarget] = useState<AccessKeyManagementType.AccessKeyManagement | null>(null)
  const [renewQuick, setRenewQuick] = useState('1Y')
  const [renewExpire, setRenewExpire] = useState<moment.Moment | null>(null)
  const [renewForm] = Form.useForm();

  // secret 下载/更新
  const [secretModalVisible, setSecretModalVisible] = useState(false)
  const [secretData, setSecretData] = useState<SecretDownloadData | null>(null)
  const [secretTitle, setSecretTitle] = useState('')

  useEffect(() => {
    if (userInfo && userInfo.person)
      loadList()
  }, []);

  useUpdateEffect(() => {
    if (appIsReady)
      loadList()
  }, [appIsReady]);

  useUpdateEffect(() => {
    setPage(1)
    loadList(1)
  }, [filter]);

  const { run: runGetAccessKeyManagementList, loading } = useRequest(getAccessKeyManagementList, {
    manual: true,
    onSuccess: (res) => {
      if (res) {
		setData(res?.openAccessKeys || [])
        setTotal(Number(res?.total) || 0)
      }
    }
  })

  const loadList = (nextPage?: number) => {
    const current = nextPage ?? page
    runGetAccessKeyManagementList({
      page: current,
      pageSize,
      status: filter === 'expired' ? 'EXPIRED' : 'ENABLED',
    })
  }

  const { run: runCreateAccessKey, loading: creating } = useRequest(CreateAccessKey, {
    manual: true,
    onSuccess: (res: any) => {
      setCreateModalVisible(false)
      setSecretData(res)
      setSecretTitle(i('创建成功'))
      setSecretModalVisible(true)
      loadList()
    },
    onError: (err: any) => {
      console.log('error', err)
      message.error(ERROR_CODE_MAP[err.response?.data?.code] || i('创建失败！'))
    }
  });
  const { run: runRenewAccessKey } = useRequest(renewAccessKey, {
    manual: true,
    onSuccess: () => {
      message.success(i('续期成功'))
      setRenewTarget(null)
      loadList()
    },
    onError: () => message.error(i('续期失败'))
  });

  const { run: runUpgradeAccessKey } = useRequest(upgradeAccessKeyToV3, {
    manual: true,
    onSuccess: () => {
      message.success(i('升级成功'))
      loadList()
    },
    onError: () => message.error(i('升级失败'))
  });

  const { run: runUpdateSecret } = useRequest(updateAccessKeySecret, {
    manual: true,
    onSuccess: (data) => {
      setSecretData(data)
      setSecretTitle(i('更新Secret成功'))
      setSecretModalVisible(true)
      loadList()
    },
    onError: () => message.error(i('更新Secret失败'))
  });

  const downloadTxt = (text: string, fileName: string) => {
    const element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text))
    element.setAttribute('download', fileName)
    element.style.display = 'none'
    element.click()
  }

  const downloadSecretFromModal = (data: SecretDownloadData) => {
    const clientSecret = data.clientSecret || ''
    const accessKeySecret = data.secret || ''
    const text = `ClientSecret, AccessKeySecret\r\n${clientSecret},${accessKeySecret}`
    downloadTxt(text, data.displayName || 'secret')
  }

  const { run: runDownloadSecret } = useRequest(downloadAccessKeySecret, {
    manual: true,
    onSuccess: (data) => {
      downloadSecretFromModal(data)
    },
    onError: (err: any) => {
      const code = err?.data?.code || err?.code
      message.error(ERROR_CODE_MAP[code] || i('下载失败'))
    }
  });

  const isExpired = (record: AccessKeyManagementType.AccessKeyManagement) => {
    if (record.status) {
      return record.status === 'EXPIRED'
    }
    return moment(record.expireTime, moment.defaultFormat).valueOf() - new Date().valueOf() <= 0
  }

  const filteredData = data.filter((item) => (filter === 'expired' ? isExpired(item) : !isExpired(item)))

  const resetCreateModal = () => {
    setCreateQuick('1Y')
    setCreateExpire(addDuration(moment(), '1Y'))
    createForm.setFieldsValue({ displayName: '', description: '', expireTime: addDuration(moment(), '1Y') })
  }

  const openCreateModal = () => {
    resetCreateModal()
    setCreateModalVisible(true)
  }

  const handleCreateQuickChange = (value: string) => {
    setCreateQuick(value)
    if (value === 'custom') {
      setCreateExpire(null)
      return
    }
    const next = addDuration(moment(), value)
    setCreateExpire(next)
    createForm.setFieldsValue({ expireTime: next })
  }

  const onFinishCreate = (values: any) => {
    const expireTime = createExpire
    if (!expireTime) {
      message.error(i('请选择过期时间'))
      return
    }
    if (expireTime.valueOf() <= new Date().valueOf()) {
      message.error(i('过期时间不能早于或等于当前时间'))
      return
    }
    runCreateAccessKey({
      displayName: values.displayName,
      description: values.description,
      expireTime: expireTime.utc().format(moment.defaultFormat)
    })
  }
  const openRenewModal = (record: AccessKeyManagementType.AccessKeyManagement) => {
    setRenewTarget(record)
    setRenewQuick('1Y')
    const base = isExpired(record) ? moment() : moment(record.expireTime, moment.defaultFormat)
    const next = addDuration(base, '1Y')
    setRenewExpire(next)
    renewForm.setFieldsValue({ expireTime: next })
  }

  const handleRenewQuickChange = (value: string) => {
    setRenewQuick(value)
    if (!renewTarget) return
    if (value === 'custom') {
      setRenewExpire(null)
      return
    }
    const base = isExpired(renewTarget) ? moment() : moment(renewTarget.expireTime, moment.defaultFormat)
    const next = addDuration(base, value)
    setRenewExpire(next)
    renewForm.setFieldsValue({ expireTime: next })
  }

  const onFinishRenew = (values: any) => {
    if (!renewTarget) return
    const expireTime = renewExpire
    if (!expireTime) {
      message.error(i('请选择过期时间'))
      return
    }
    const original = moment(renewTarget.expireTime, moment.defaultFormat).valueOf()
    const now = new Date().valueOf()
    const min = isExpired(renewTarget) ? now : original
    if (expireTime.valueOf() <= min) {
      message.error(isExpired(renewTarget) ? i('新时间须晚于当前时间') : i('新时间须晚于原过期时间'))
      return
    }
    runRenewAccessKey({
      openAccessKeyId: renewTarget.id,
      expireTime: expireTime.utc().format(moment.defaultFormat)
    })
  }

  const confirmUpgrade = (record: AccessKeyManagementType.AccessKeyManagement) => {
    Modal.confirm({
      title: i('升级至V3'),
      icon: <ExclamationCircleOutlined />,
      content: i('确认升级后，系统将根据账号绑定关系获取设备，并开放V3全部接口能力。原ClientID、ClientSecret、AccessSecret可继续使用。该升级操作不可逆，请谨慎确认。'),
      okText: i('确认升级'),
      cancelText: i('取消'),
      onOk: () => runUpgradeAccessKey({ openAccessKeyId: record.id }),
    })
  }

  const confirmUpdateSecret = (record: AccessKeyManagementType.AccessKeyManagement) => {
    Modal.confirm({
      title: i('更新Secret'),
      content: i('更新后原Secret将立即失效，请确认是否继续？'),
      okText: i('确认更新'),
      cancelText: i('取消'),
      onOk: () => runUpdateSecret({ openAccessKeyId: record.id }),
    })
  }

  const onDelete = async (openAccessKeyId: string) => {
    try {
      const res = await deleteAccessKey({ openAccessKeyId })
      if (res.code === 0) {
        message.success(i("删除成功"))
        loadList()
      } else {
        message.error(ERROR_CODE_MAP[res.code] || i("出错啦"))
      }
    } catch (error) {
      message.error(i("删除失败"))
    }
  }

  const confirmDelete = (openAccessKeyId: string) => {
    Modal.confirm({
      title: i("提示"),
      content: i("您是否确认删除，会影响已有的开发对接成果？"),
      onOk: () => onDelete(openAccessKeyId),
      okText: i("确定"),
      cancelText: i("取消"),
      onCancel: () => Modal.destroyAll()
    })
  }
  const renderActions = (record: AccessKeyManagementType.AccessKeyManagement) => {
    const expired = isExpired(record)
    const isPureV3 = record.version === 'V3' && !!record.clientId
    const isV1 = record.version === 'V1'
    return (
      <div className="row-actions">
        <a onClick={() => openRenewModal(record)}>{i("续期")}</a>
        {isPureV3 && !expired && (
          <>
            <a onClick={() => confirmUpdateSecret(record)}>{i("更新Secret")}</a>
            <a onClick={() => runDownloadSecret({ openAccessKeyId: record.id })}>{i("下载Secret")}</a>
          </>
        )}
        {isV1 && (
          <a style={{ color: 'red' }} onClick={() => confirmUpgrade(record)}>{i("升级至V3")}</a>
        )}
        <a style={{ color: 'red' }} onClick={() => confirmDelete(record.id)}>{i("删除")}</a>
      </div>
    )
  }

  const columns: any = [
    {
      title: i('AccessKey'),
      dataIndex: 'id',
      width: 240,
      render: (text: string, record: any) => (
        <>
          <span style={{ wordBreak: 'break-all' }}>{text}</span>
          <Tag color={record.version === 'V3' ? 'blue' : 'default'} style={{ marginLeft: 8 }}>{record.version}</Tag>
        </>
      )
    },
    {
      title: i('ClientID'),
      dataIndex: 'clientId',
    //   width: 140,
      render: (text: string) => text || '—'
    },
    {
      title: i('说明'),
      key: 'desc',
      width: 220,
      render: (_: any, record: any) => (
        <>
          <Tooltip title={record.displayName}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.displayName}</div>
          </Tooltip>
          <Tooltip title={record.description}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(0,0,0,.45)' }}>{record.description}</div>
          </Tooltip>
        </>
      )
    },
    {
      title: i('创建时间'),
      dataIndex: 'createTime',
      width: 200,
      render: (text: string) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: i('过期时间'),
      dataIndex: 'expireTime',
      width: 200,
      render: (text: string) => moment(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: i("操作"),
      key: "operation",
      width: 280,
      fixed: 'right',
      render: (_: any, record: any) => renderActions(record)
    }
  ];
  const secretDownloadJsx = (
    <Modal
      title={secretTitle}
      visible={secretModalVisible}
      closable={true}
      onCancel={() => setSecretModalVisible(false)}
      footer={null}
      centered
    >
      <div className="secret-success-body">
        <ExclamationCircleOutlined className="secret-success-icon" />
        <p className="secret-success-msg">{i("Secret已生成，可立即下载保存；后续可在列表中下载或更新Secret。")}</p>
      </div>
      <div className="secret-success-actions">
        <Button onClick={() => {
          if (!secretData) return
          downloadSecretFromModal(secretData)
        }}>
          {i('下载Secret')}
        </Button>
        <Button type="primary" onClick={() => setSecretModalVisible(false)}>{i('关闭')}</Button>
      </div>
    </Modal>
  );

  const createAccessKeyModalJsx = (
    <Modal
      title={<span>{i("创建AccessKey")} <Tag color="blue">V3</Tag></span>}
      maskClosable={false}
      visible={createModalVisible}
      footer={false}
      onCancel={() => { setCreateModalVisible(false) }}
    >
      <Alert
        type="info"
        showIcon
        message={i("V3 版 AccessKey 仅可获取当前开发者账号已绑定的机器。")}
        style={{ marginBottom: 16 }}
      />
      <Form form={createForm} name="createAccessKey" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} onFinish={onFinishCreate} autoComplete="off" labelWrap={true}>
        <Form.Item label={i("名称")} name="displayName" rules={[{ required: true, message: i('请填写当前创建的AccessKey的别名！') }]}>
          <Input showCount maxLength={100} />
        </Form.Item>
        <Form.Item label={i("描述")} name="description" rules={[{ required: true, message: i('请填写当前创建的AccessKey的描述！') }]}>
          <Input showCount maxLength={50} />
        </Form.Item>
        <Form.Item label={i("过期时间")} required>
          <Radio.Group
            value={createQuick}
            onChange={(e) => handleCreateQuickChange(e.target.value)}
            optionType="button"
            options={getDurationOptions()}
            style={{ marginBottom: 8 }}
          />
          {createQuick === 'custom' ? (
            <DatePicker
              value={createExpire}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              onChange={(v) => { if (v) setCreateExpire(v) }}
              style={{ width: '100%' }}
            />
          ) : (
            <Input
              readOnly
              value={createExpire ? createExpire.format('YYYY-MM-DD HH:mm:ss') : ''}
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
          <Button type="primary" htmlType="submit" loading={creating}>
            {i("提交")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );

  const renewModalJsx = (
    <Modal
      title={i("续期")}
      maskClosable={false}
      visible={!!renewTarget}
      footer={false}
      onCancel={() => setRenewTarget(null)}
    >
      <Form form={renewForm} name="renewAccessKey" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} onFinish={onFinishRenew} autoComplete="off" labelWrap={true}>
        <Form.Item label={i("过期时间")} required>
          <Radio.Group
            value={renewQuick}
            onChange={(e) => handleRenewQuickChange(e.target.value)}
            optionType="button"
            options={getDurationOptions()}
            style={{ marginBottom: 8 }}
          />
          {renewQuick === 'custom' ? (
            <DatePicker
              value={renewExpire}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              onChange={(v) => { if (v) setRenewExpire(v) }}
              style={{ width: '100%' }}
            />
          ) : (
            <Input
              readOnly
              value={renewExpire ? renewExpire.format('YYYY-MM-DD HH:mm:ss') : ''}
              style={{ width: '100%' }}
            />
          )}
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
          <Button type="primary" htmlType="submit">
            {i("确认续期")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );

  const handleCreate = () => {
    openCreateModal()
  }

  return (
    <div className='main'>
      {secretDownloadJsx}
      {createAccessKeyModalJsx}
      {renewModalJsx}
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} optionType="button" buttonStyle="solid">
          <Radio.Button value="enabled">{i("已启用")}</Radio.Button>
          <Radio.Button value="expired">{i("已过期")}</Radio.Button>
        </Radio.Group>
        <span style={{ flex: 1 }} />
        <Button onClick={handleCreate} type="primary">
          {i("创建")}
        </Button>
        <Button onClick={() => loadList()} type="primary">
          {i("刷新列表")}
        </Button>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        loading={loading}
        dataSource={filteredData}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p) => {
            setPage(p)
            loadList(p)
          },
        }}
        scroll={{ x: 1300 }}
      />
    </div>
  );
}

export default AccessKeyManagement
